import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { CommandRegistry, type CommandContext, type CommandSpec } from '../lib/command-registry';
import { useFormattedShortcut } from '../hooks/useCommandRegistry';
import { loadRecentCommands, pushRecentCommand } from '../lib/command-palette-recents';

/** Cmd/Ctrl+K command palette (Tier 3 Group C Phase 1).
 *
 *  Single modal that searches every registered editor command.
 *  Opens via the `palette.open` command (registered by EditorPage);
 *  the palette itself only owns its internal key handling once open.
 *
 *  Keyboard contract (input retains DOM focus throughout):
 *    Esc         → onClose
 *    ↑ / ↓       → move selection
 *    Home / End  → jump to first / last
 *    Enter       → dispatch selected + close
 *
 *  Mouse contract:
 *    hover       → update selection (so the visible highlight tracks
 *                  the pointer, matching VSCode + Linear conventions)
 *    click       → dispatch + close
 *    click outside the modal → close
 *
 *  ARIA: combobox pattern. The search input is the combobox; the
 *  results div is the listbox; focus stays on the input and
 *  `aria-activedescendant` points to the selected option. */
export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  registry: CommandRegistry;
  context: CommandContext;
  isMac: boolean;
  /** Test seam — when provided, overrides localStorage-backed
   *  recents. Production callers omit it. */
  recentsStorage?: Storage;
}

export function CommandPalette({
  open,
  onClose,
  registry,
  context,
  isMac,
  recentsStorage,
}: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recents, setRecents] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const listboxId = useId();

  // Snapshot recents at open-time. Re-reading on every render would
  // race the push-on-dispatch effect, so we reload only when the
  // modal transitions open and after a successful dispatch.
  useEffect(() => {
    if (open) setRecents(loadRecentCommands(recentsStorage));
  }, [open, recentsStorage]);

  // Reset on open. Capture previously-focused element so we can
  // restore focus to it on close — important for keyboard users who
  // would otherwise lose their place after dismissing the palette.
  useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current = (document.activeElement as HTMLElement) ?? null;
    setQuery('');
    setSelectedIndex(0);
    // Focus the input on the next tick — running it synchronously
    // races the conditional mount.
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  // Restore focus to the previously-active element on close. The
  // `isConnected` check guards against the prior focus target having
  // been unmounted while the palette was open (e.g. the user
  // dispatched a command that closed the host panel) — without it,
  // focus.focus() would silently move focus to <body> and trap
  // keyboard users with no obvious recovery path.
  useEffect(() => {
    if (open) return;
    const prev = previouslyFocusedRef.current;
    if (prev && typeof prev.focus === 'function' && prev.isConnected) {
      prev.focus();
    }
    previouslyFocusedRef.current = null;
  }, [open]);

  /** Visible rows. When query is empty we show recents first, then
   *  every other registered command in declaration order. When the
   *  query is non-empty we show the scored search results only —
   *  recents drop out so the user isn't distracted by their history
   *  while typing. */
  const rows: PaletteRow[] = useMemo(() => {
    if (!open) return [];
    if (query.trim().length === 0) {
      const visible = registry.list().filter((c) => !c.hidden);
      const byId = new Map(visible.map((c) => [c.id, c]));
      const recentCommands = recents.map((id) => byId.get(id)).filter((c): c is CommandSpec => !!c);
      const recentIds = new Set(recentCommands.map((c) => c.id));
      const others = visible.filter((c) => !recentIds.has(c.id));
      return [
        ...recentCommands.map((c) => ({ command: c, group: 'recent' as const })),
        ...others.map((c) => ({ command: c, group: 'all' as const })),
      ];
    }
    return registry.search(query).map(({ command }) => ({ command, group: 'all' as const }));
  }, [open, query, registry, recents]);

  // Keep selectedIndex inside the row bounds when results change
  // (typing narrows the list — moving selection to top is least
  // surprising). Skip the reset when results are simply re-sorted at
  // the same length to preserve scroll position.
  const rowCount = rows.length;
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);
  useEffect(() => {
    if (selectedIndex >= rowCount) setSelectedIndex(Math.max(0, rowCount - 1));
  }, [rowCount, selectedIndex]);

  // Scroll the selected option into view when the keyboard moves it.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-row-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [open, selectedIndex]);

  if (!open) return null;

  const handleSelect = async (command: CommandSpec) => {
    onClose();
    const next = pushRecentCommand(command.id, recentsStorage);
    setRecents(next);
    await registry.dispatch(command.id, context);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => (rowCount === 0 ? 0 : Math.min(rowCount - 1, i + 1)));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      setSelectedIndex(0);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      setSelectedIndex(Math.max(0, rowCount - 1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const row = rows[selectedIndex];
      if (row) void handleSelect(row.command);
      return;
    }
  };

  const activeOptionId = rows[selectedIndex] ? `${listboxId}-opt-${selectedIndex}` : undefined;

  // Track whether we're currently showing the recents header — we
  // need to break the row list into two visual sections.
  const firstNonRecentIndex = rows.findIndex((r) => r.group === 'all');
  const hasRecentsSection =
    query.trim().length === 0 && recents.length > 0 && firstNonRecentIndex !== 0;

  return (
    <div
      // Portrait (mobile / narrow): full-width bottom sheet anchored
      //   to the bottom edge — items-end + no top padding.
      // Landscape / tablet / desktop (sm: and up): the classic
      //   command-palette centered card 20vh from the top, matches
      //   VSCode / Linear conventions.
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-start sm:pt-[20vh]"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-label="Command palette"
        aria-modal="true"
        // Focus trap — keep Tab cycling between the input and the
        // close button instead of letting it escape to the editor
        // behind the modal. Option rows are dispatch-on-click /
        // arrow-key targets, not Tab stops, so the cycle has just
        // two members.
        onKeyDown={(e) => {
          if (e.key !== 'Tab') return;
          const focusable: HTMLElement[] = [];
          if (inputRef.current) focusable.push(inputRef.current);
          if (closeButtonRef.current) focusable.push(closeButtonRef.current);
          if (focusable.length === 0) return;
          const active = document.activeElement as HTMLElement | null;
          const currentIdx = active ? focusable.indexOf(active) : -1;
          const nextIdx = e.shiftKey
            ? currentIdx <= 0
              ? focusable.length - 1
              : currentIdx - 1
            : currentIdx === focusable.length - 1
              ? 0
              : currentIdx + 1;
          e.preventDefault();
          focusable[nextIdx]?.focus();
        }}
        className={[
          'bg-background shadow-xl flex flex-col overflow-hidden',
          // Portrait bottom-sheet: full width, only top corners
          // rounded, only top + side borders (bottom edge is flush
          // with the viewport). pb-[env(safe-area-inset-bottom)]
          // keeps content above the iOS home-indicator gesture
          // strip; the padding inherits to the scrolling list so
          // its last row is reachable.
          'w-full max-h-[85vh] rounded-t-xl border-t border-x border-border pb-[env(safe-area-inset-bottom)]',
          // sm: cancel the bottom-sheet treatment — go back to the
          // centered card with all-rounded corners and a normal
          // border on every side.
          'sm:w-[92vw] sm:max-w-2xl sm:max-h-[70vh] sm:rounded-lg sm:border sm:pb-0',
        ].join(' ')}
        onClick={(e) => e.stopPropagation()}
      >
        {/* iOS-style grab handle on portrait — purely decorative,
            signals "this is a draggable sheet" even though we don't
            yet wire up drag-to-dismiss (v1 keeps tap-backdrop /
            Esc as the dismiss surface). Hidden on sm: where the
            modal is a normal centered card. */}
        <div
          aria-hidden="true"
          className="mx-auto mt-2 mb-1 h-1 w-10 rounded-full bg-muted-foreground/30 sm:hidden"
        />
        <div className="flex items-center gap-2 px-4 border-b border-border shrink-0">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder="Type a command or search..."
            aria-label="Command palette search"
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={true}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={activeOptionId}
            className="flex-1 bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
          />
          <button
            ref={closeButtonRef}
            type="button"
            aria-label="Close command palette"
            className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {/* SR-only live region announces result-count changes as the
            user types — without this, screen-reader users hear no
            feedback when the list narrows. Visually hidden via the
            sr-only utility; updates trigger a polite announcement. */}
        <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {query.trim().length > 0
            ? rowCount === 0
              ? `No commands match ${query}`
              : `${rowCount} command${rowCount === 1 ? '' : 's'} match ${query}`
            : ''}
        </div>
        <div
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-label="Commands"
          className="overflow-y-auto py-1"
        >
          {rowCount === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground text-center">
              No commands match "{query}".
            </div>
          ) : (
            <>
              {hasRecentsSection && <SectionHeader>Recent</SectionHeader>}
              {rows.map((row, idx) => {
                const showAllHeader = hasRecentsSection && idx === firstNonRecentIndex;
                return (
                  <div key={`${row.command.id}-${idx}`}>
                    {showAllHeader && <SectionHeader>All commands</SectionHeader>}
                    <PaletteOption
                      row={row}
                      isSelected={idx === selectedIndex}
                      isMac={isMac}
                      registry={registry}
                      isAvailable={
                        row.command.isAvailable ? row.command.isAvailable(context) : true
                      }
                      optionId={`${listboxId}-opt-${idx}`}
                      rowIndex={idx}
                      onHover={() => setSelectedIndex(idx)}
                      onClick={() => void handleSelect(row.command)}
                    />
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

interface PaletteRow {
  command: CommandSpec;
  group: 'recent' | 'all';
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  // role="presentation" would hide the section name from assistive
  // tech entirely — but screen-reader users benefit from knowing
  // when the list shifts from "Recent" to "All commands". role +
  // aria-level expose it as a level-3 heading inside the dialog
  // landmark, matching how VSCode + Linear annotate their palettes.
  return (
    <div
      role="heading"
      aria-level={3}
      className="px-4 pt-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
    >
      {children}
    </div>
  );
}

interface PaletteOptionProps {
  row: PaletteRow;
  isSelected: boolean;
  isAvailable: boolean;
  isMac: boolean;
  registry: CommandRegistry;
  optionId: string;
  rowIndex: number;
  onHover: () => void;
  onClick: () => void;
}

function PaletteOption({
  row,
  isSelected,
  isAvailable,
  isMac,
  registry,
  optionId,
  rowIndex,
  onHover,
  onClick,
}: PaletteOptionProps) {
  const { command } = row;
  // Reactive read — picks up user overrides from Settings →
  // Keyboard the instant they're committed (Tier 3 Group C Phase 2).
  const shortcut = useFormattedShortcut(registry, command.id, isMac);
  return (
    <div
      role="option"
      id={optionId}
      data-row-index={rowIndex}
      aria-selected={isSelected}
      aria-disabled={!isAvailable}
      onMouseEnter={onHover}
      onClick={isAvailable ? onClick : undefined}
      className={[
        'flex items-center gap-3 px-4 py-2 text-sm cursor-pointer',
        isSelected ? 'bg-accent' : '',
        isAvailable ? 'text-foreground' : 'text-muted-foreground cursor-not-allowed',
      ].join(' ')}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate">{command.label}</span>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {command.category}
          </span>
        </div>
        {command.description && (
          <div className="text-xs text-muted-foreground truncate">{command.description}</div>
        )}
      </div>
      {shortcut && (
        <kbd className="text-xs font-mono text-muted-foreground shrink-0">{shortcut}</kbd>
      )}
    </div>
  );
}
